import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Group,
  Stack,
  Text,
  TextInput,
  Button,
  Divider,
  Select,
  MultiSelect,
  Box,
  Loader,
  SimpleGrid,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconFilter, IconSearch, IconTrash } from "@tabler/icons-react";

import { fetch_category } from "../../services/Category.api";
import { fetchClientSubjects, fetchClientClippingSelections } from "../../services/Client.api";
import { fetchSources } from "../../services/Sources";

export default function Filters({ onApply, value }) {
  const clientId = useMemo(() => localStorage.getItem("clientId"), []);

  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0, 0);
  }, []);

  const datePresets = useMemo(() => {
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const last7Start = new Date(today);
    last7Start.setDate(today.getDate() - 6);

    const firstDayCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1, 12, 0, 0, 0);
    const lastDayPreviousMonth = new Date(firstDayCurrentMonth.getTime() - 86400000);
    const firstDayPreviousMonth = new Date(
      lastDayPreviousMonth.getFullYear(),
      lastDayPreviousMonth.getMonth(),
      1,
      12,
      0,
      0,
      0
    );

    return [
      {
        value: [today, today],
        label: "Hoje",
      },
      {
        value: [yesterday, yesterday],
        label: "Ontem",
      },
      {
        value: [last7Start, today],
        label: "Últimos 7 dias",
      },
      {
        value: [firstDayPreviousMonth, lastDayPreviousMonth],
        label: "Mês anterior",
      },
    ];
  }, [today]);

  const [range, setRange] = useState([today, today]);

  const [media, setMedia] = useState([]);
  const [evaluation, setEvaluation] = useState("all");
  const [sentiment, setSentiment] = useState("neutral");

  const [category, setCategory] = useState(null);
  const [subject, setSubject] = useState(null);
  const [sources, setSources] = useState([]);
  const [selection, setSelection] = useState(null);
  const [text, setText] = useState("");

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectionOptions, setSelectionOptions] = useState([]);

  const [subjectOptions, setSubjectOptions] = useState([]);
  const [subjectSearch, setSubjectSearch] = useState("");
  const [subjectLoading, setSubjectLoading] = useState(false);

  const [sourceOptions, setSourceOptions] = useState([]);
  const [sourceSearch, setSourceSearch] = useState("");
  const [sourceLoading, setSourceLoading] = useState(false);

  const sentimentEnabled = evaluation === "evaluated";

  const normalizeMediaValue = (mediaValue) => {
    if (Array.isArray(mediaValue)) return mediaValue.filter(Boolean);
    if (!mediaValue || mediaValue === "all") return [];

    return String(mediaValue)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const fromISOToLocalNoon = (iso) => {
    if (!iso) return null;
    const [y, m, d] = String(iso).split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d, 12, 0, 0, 0);
  };

  const normalizePickerDate = (v) => {
    if (!v) return null;

    const dt = v instanceof Date ? v : new Date(v);
    if (Number.isNaN(dt.getTime())) return null;

    return new Date(
      dt.getUTCFullYear(),
      dt.getUTCMonth(),
      dt.getUTCDate(),
      12, 0, 0, 0
    );
  };

  const toISO = (v) => {
    const dt = normalizePickerDate(v);
    if (!dt) return null;

    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  useEffect(() => {
    if (!value) return;

    const start = fromISOToLocalNoon(value.startDate);
    const end = fromISOToLocalNoon(value.endDate);
    if (start && end) setRange([start, end]);

    setMedia(normalizeMediaValue(value.media));
    if (value.evaluation) setEvaluation(value.evaluation);

    if (value.sentiment) setSentiment(value.sentiment);
    else setSentiment("neutral");

    setCategory(value.category ?? null);
    setSubject(value.subject ?? null);
    setSources(Array.isArray(value.sources) ? value.sources : []);
    setSelection(value.selection ?? null);
    setText(value.text ?? "");
  }, [value]);

  useEffect(() => {
    if (!sentimentEnabled) setSentiment("neutral");
  }, [sentimentEnabled]);

  const [start, end] = range || [];
  const payload = {
    clientId,
    startDate: toISO(start),
    endDate: toISO(end),

    media,
    evaluation,
    sentiment: sentimentEnabled ? sentiment : null,

    category,
    subject,
    sources,
    selection,
    text: text.trim(),
  };

  useEffect(() => {
    let mounted = true;

    async function boot() {
      try {
        if (!clientId) return;

        const [cats, sels] = await Promise.all([
          fetch_category(clientId),
          fetchClientClippingSelections(clientId),
        ]);

        if (!mounted) return;

        const catArr = Array.isArray(cats) ? cats : (cats?.results || []);
        setCategoryOptions(
          catArr.map((c) => ({
            value: String(c.id),
            label: String(c.name || c.title || c.label || `Categoria ${c.id}`),
          }))
        );

        const selArr = Array.isArray(sels) ? sels : (sels?.results || []);
        setSelectionOptions(
          selArr.map((s) => ({
            value: String(s.id),
            label: String(s.name || s.title || s.label || `Seleção ${s.id}`),
          }))
        );
      } catch (e) {
        console.error("Erro ao carregar filtros iniciais:", e);
      }
    }

    boot();
    return () => { mounted = false; };
  }, [clientId]);

  useEffect(() => {
    let mounted = true;

    async function loadSubjects() {
      try {
        if (!clientId) return;
        setSubjectLoading(true);
        const res = await fetchClientSubjects(clientId, subjectSearch, 20);
        if (!mounted) return;
        setSubjectOptions(res || []);
      } finally {
        if (mounted) setSubjectLoading(false);
      }
    }

    if ((subjectSearch || "").trim().length >= 3) loadSubjects();
    else setSubjectOptions([]);

    return () => { mounted = false; };
  }, [clientId, subjectSearch]);

  useEffect(() => {
    let mounted = true;

    async function loadSources() {
      try {
        setSourceLoading(true);
        const res = await fetchSources(sourceSearch);
        if (!mounted) return;

        const arr = Array.isArray(res) ? res : (res?.results || []);
        setSourceOptions(
          arr.map((s) => ({
            value: String(s.id),
            label: String(s.name || s.title || `Veículo ${s.id}`),
          }))
        );
      } catch (e) {
        console.error("Erro ao buscar veículos:", e);
        if (mounted) setSourceOptions([]);
      } finally {
        if (mounted) setSourceLoading(false);
      }
    }

    const term = (sourceSearch || "").trim();
    if (term.length >= 2) loadSources();
    else setSourceOptions([]);

    return () => { mounted = false; };
  }, [sourceSearch]);

  function handleClear() {
    setRange([today, today]);
    setMedia([]);

    setEvaluation("all");
    setSentiment("neutral");

    setCategory(null);
    setSubject(null);
    setSources([]);
    setSelection(null);
    setText("");

    setSubjectSearch("");
    setSourceSearch("");

    onApply?.({
      clientId,
      startDate: toISO(today),
      endDate: toISO(today),
      media: [],
      evaluation: "all",
      sentiment: null,
      category: null,
      subject: null,
      sources: [],
      selection: null,
      text: "",
    });
  }

  function handleApply() {
    onApply?.(payload);
  }

  const commonInputStyles = {
    root: {
      "--input-bd": "var(--border-filter-color)",
      "--input-bd-focus": "var(--icon-menu-active-color)",
      "--input-shadow": "0 0 0 3px rgba(194, 15, 104, 0.18)",
    },

    label: {
      fontWeight: 800,
      fontSize: 12,
      color: "var(--color-text-filter)",
    },

    input: {
      height: 38,
      backgroundColor: "var(--bottom-dropdown-color)",
      color: "var(--text-color)",

      "&:hover": {
        borderColor: "var(--icon-menu-active-color)",
      },
    },

    dropdown: {
      backgroundColor: "var(--bottom-dropdown-color)",
      borderColor: "var(--border-filter-color)",
    },

    option: {
      color: "var(--text-color)",

      "&[data-hovered]": {
        backgroundColor: "var(--bottom-menu-active-color)",
        color: "var(--text-color)",
      },

      "&[data-selected]": {
        backgroundColor: "var(--icon-menu-active-color)",
        color: "var(--color-text-button-action)",
      },
    },
  };

  return (
    <Card
      withBorder
      radius="lg"
      p="md"
      style={{
        background: "var(--color-filter)",
        borderColor: "var(--border-filter-color)",
      }}
    >
      <Group justify="space-between" align="flex-start" gap="md">
        <Group gap="sm" align="center">
          <Box
            style={{
              width: 36,
              height: 36,
              borderRadius: 12,
              display: "grid",
              placeItems: "center",
              background: "linear-gradient(135deg, var(--icon-gradient-start), var(--icon-gradient-end))",
              color: "var(--color-text-button-action)",
            }}
          >
            <IconFilter size={18} />
          </Box>

          <Stack gap={2}>
            <Text fw={800} size="sm" style={{ color: "var(--text-color)" }}>
              Filtros
            </Text>
            <Text size="xs" style={{ color: "var(--color-text-filter)" }}>
              Período, tipo de veículo, avaliação, polaridade, categoria, assunto, veículos e seleções
            </Text>
          </Stack>
        </Group>

        <Group gap="sm">
          <Button
            variant="filled"
            radius="md"
            onClick={handleClear}
            leftSection={<IconTrash size={16} />}
            styles={{
              root: {
                backgroundColor: "var(--color-button-cancel)",
                color: "var(--color-text-button-cancel)",
                border: `1px solid var(--color-border-button-cancel)`,
              },
            }}
          >
            Limpar
          </Button>

          <Button
            variant="filled"
            radius="md"
            onClick={handleApply}
            styles={{
              root: {
                backgroundColor: "var(--color-button-action)",
                color: "var(--color-text-button-action)",
              },
            }}
          >
            Aplicar
          </Button>
        </Group>
      </Group>

      <Divider
        my="md"
        styles={{
          root: { borderColor: "var(--border-filter-color)" },
        }}
      />

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md" verticalSpacing="md">
        <DatePickerInput
          type="range"
          label="Período"
          placeholder="Selecione o intervalo"
          value={range}
          onChange={(value) => {
            const [s, e] = value || [];
            setRange([normalizePickerDate(s), normalizePickerDate(e)]);
          }}
          valueFormat="DD/MM/YYYY"
          radius="md"
          size="sm"
          presets={datePresets}
          styles={{
            ...commonInputStyles,
            input: {
              ...commonInputStyles.input,
              backgroundColor: "var(--bottom-dropdown-color)",
            },
          }}
        />

        <Stack gap={6}>

          <MultiSelect
            label="Tipo de veículo"
            value={media}
            onChange={setMedia}
            placeholder="Selecione um ou mais"
            radius="md"
            size="sm"
            clearable
            data={[
              { label: "Impresso", value: "newspaper" },
              { label: "Rádio", value: "radio" },
              { label: "TV", value: "tv" },
              { label: "Site", value: "site" },
              { label: "Blog", value: "blog" },
            ]}
            styles={commonInputStyles}
            fullWidth
          />
        </Stack>

        <Select
          label="Avaliação"
          value={evaluation}
          onChange={setEvaluation}
          data={[
            { value: "all", label: "Todas" },
            { value: "evaluated", label: "Avaliadas" },
            { value: "not_evaluated", label: "Não avaliadas" },
          ]}
          radius="md"
          size="sm"
          styles={commonInputStyles}
        />

        <Select
          label="Polaridade"
          value={sentiment}
          onChange={setSentiment}
          disabled={!sentimentEnabled}
          placeholder={sentimentEnabled ? "Selecione" : "Escolha 'Avaliadas'"}
          data={[
            { value: "positive", label: "Positivo" },
            { value: "neutral", label: "Neutro" },
            { value: "negative", label: "Negativo" },
          ]}
          radius="md"
          size="sm"
          styles={{
            ...commonInputStyles,
            input: {
              ...commonInputStyles.input,
              backgroundColor: sentimentEnabled ? "var(--bottom-dropdown-color)" : "var(--disabled-color)",
              color: sentimentEnabled ? "var(--text-color)" : "var(--text-disabled-color)",
            },
          }}
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 5 }} spacing="md" verticalSpacing="md" style={{ marginTop: 16 }}>
        <TextInput
          label="Palavras a Localizar"
          placeholder="Digite um termo"
          value={text}
          onChange={(event) => setText(event.currentTarget.value)}
          leftSection={<IconSearch size={16} />}
          radius="md"
          size="sm"
          styles={commonInputStyles}
        />

        <Select
          label="Categoria"
          placeholder="Selecione"
          value={category}
          onChange={setCategory}
          data={categoryOptions}
          searchable
          clearable
          radius="md"
          size="sm"
          nothingFoundMessage="Nenhuma categoria"
          styles={commonInputStyles}
        />

        <Select
          label="Assunto"
          placeholder="Digite 3+ letras…"
          value={subject}
          onChange={setSubject}
          data={subjectOptions}
          searchable
          clearable
          searchValue={subjectSearch}
          onSearchChange={setSubjectSearch}
          rightSection={subjectLoading ? <Loader size="xs" /> : null}
          radius="md"
          size="sm"
          nothingFoundMessage={
            (subjectSearch || "").trim().length < 3
              ? "Digite pelo menos 3 letras"
              : "Nenhum assunto encontrado"
          }
          styles={commonInputStyles}
        />

        <MultiSelect
          label="Veículos"
          placeholder="Digite 2+ letras…"
          value={sources}
          onChange={setSources}
          data={sourceOptions}
          searchable
          clearable
          searchValue={sourceSearch}
          onSearchChange={setSourceSearch}
          rightSection={sourceLoading ? <Loader size="xs" /> : null}
          radius="md"
          size="sm"
          nothingFoundMessage={
            (sourceSearch || "").trim().length < 2
              ? "Digite pelo menos 2 letras"
              : "Nenhum veículo encontrado"
          }
          styles={{
            ...commonInputStyles,
            input: {
              ...commonInputStyles.input,
              minHeight: 38,
            },
          }}
        />

        <Select
          label="Seleções salvas"
          placeholder="Selecione"
          value={selection}
          onChange={setSelection}
          data={selectionOptions}
          searchable
          clearable
          radius="md"
          size="sm"
          nothingFoundMessage="Nenhuma seleção salva"
          styles={commonInputStyles}
        />
      </SimpleGrid>
    </Card>
  );
}
